import { api, LightningElement, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";

import { EffectAdapter } from "common/effectAdapter";
import { fetchAsset, getAssetsUrl } from "common/helpers";

type Size = "x-small" | "small" | "medium" | "large" | "xlarge" | "override";

export type Sprite =
  | "action"
  | "custom"
  | "doctype"
  | "social"
  | "social-round"
  | "trailhead"
  | "utility";

export const DEFAULT_SPRITE: Sprite = "utility";

const fetches = new Map<string, ReturnType<typeof fetchAsset>>();

export default class extends LightningElement {
  @api sprite: Sprite = DEFAULT_SPRITE;
  @api size: Size = "medium";
  @api symbol: string = "";
  @api ariaHidden: string = "true";
  @api assistiveText: string | null = null;

  private node: SVGSymbolElement | undefined;

  @wire(EffectAdapter, { sprite: "$sprite", symbol: "$symbol" })
  private async onChange() {
    if (this.willFetch) {
      this.appendNodes().catch((e: Error) => logger.warn(e.message));
    }
  }

  private get className() {
    return `size--${this.size}`;
  }

  private get spriteUrl() {
    // eslint-disable-next-line local/import-image-path
    return getAssetsUrl(`/icons/${this.sprite}-sprite/svg/symbols.svg`);
  }

  private get sdsUrl() {
    // eslint-disable-next-line local/import-image-path
    return `https://api.lightningdesignsystem.com/sds/icons/foundation/${this.symbol}.svg`;
  }

  // Use the SDS CDN if available
  private get willUseSDS() {
    return false;
    // TODO re-enable once salesforce-ux has properly tuned their CDN
    // return this.sprite === "utility";
  }

  private get willFetch() {
    return this.willUseSDS || /^http/.test(this.spriteUrl);
  }

  private get href() {
    return `${this.spriteUrl}#${this.symbol}`;
  }

  private get symbolId() {
    return `tds__${this.sprite}__${this.symbol}`;
  }

  private get svgAssistiveText() {
    // For backward compatibility, falls back to the ariaLabel
    return this.assistiveText || this.ariaLabel;
  }

  renderedCallback() {
    if (this.willFetch) {
      this.appendNodes().catch((e: Error) => logger.warn(e.message));
    }
  }

  /**
   * Fetch the icon and append svg code to the DOM
   */
  private async appendNodes() {
    // Don't re-fetch if the icon is already set
    if (this.node?.id === this.symbolId) return;

    const svg = this.template.querySelector("svg")!;
    const icon = await this.fetchIcon();

    // Warn if icon couldn't be fetched
    if (!icon) {
      logger.error(`Could not load icon symbol "${this.symbol}".`);
      return;
    }

    const node = icon.cloneNode(true) as SVGSymbolElement;

    // Remove previous nodes
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    // Insert new nodes
    for (let n of Array.from(node.childNodes)) svg.appendChild(n);
    svg.setAttribute("viewBox", node.getAttribute("viewBox")!);

    this.node = node;
  }

  private async fetchIcon() {
    return this.willUseSDS
      ? this.fetchFromSDS().catch(() => {
          // Fallback to sprite
          return this.fetchFromSprite();
        })
      : this.fetchFromSprite();
  }

  private async fetchFromSDS() {
    let promise;
    if (fetches.has(this.sdsUrl)) {
      promise = fetches.get(this.sdsUrl)!;
    } else {
      promise = fetchAsset(this.sdsUrl);
      fetches.set(this.sdsUrl, promise);
    }

    return promise.then(async (code) => {
      const div = document.createElement("div");
      div.innerHTML = code;
      const svg = div.firstElementChild!;
      // Ensure fill color is set properly
      Array.from(svg.querySelectorAll("[fill]"))?.forEach((n) => {
        n.setAttribute("fill", "currentColor");
      });
      return svg;
    });
  }

  private async fetchFromSprite() {
    let promise;
    if (fetches.has(this.spriteUrl)) {
      promise = fetches.get(this.spriteUrl)!;
    } else {
      const spriteUrl = this.spriteUrl;
      const sprite = this.sprite;
      promise = fetchAsset(spriteUrl)
        .then((code) => {
          return code.replace(/id="/g, `id="tds__${sprite}__`);
        })
        .then((code) => {
          const div = document.createElement("div");
          div.style.display = "none";
          div.innerHTML = code;
          document.body.appendChild(div);
          return code;
        });
      fetches.set(spriteUrl, promise);
    }

    return promise.then(() => {
      return document.querySelector(`#${this.symbolId}`);
    });
  }
}
